if (!customElements.get('product-overview-frame-preview')) {
  class ProductOverviewFramePreview extends HTMLElement {
    constructor() {
      super();

      this.body = document.querySelector('body');
      this.productCardsWrapper = this.body.querySelector('[data-main-product-overview]');

      // get all the frame/match artwork options, product card wrapper & match artwork switch state
      this.frameOptions = this.querySelectorAll('[data-frame-selector]');
      this.matchArtworkSwitches = this.querySelectorAll('[data-match-artwork-switch]');
      this.isMatchArtworkEnabled = false;

      // check if there're promo blocks with frame options and/or match artwork to add to the array
      this.framePromoOptions = this.productCardsWrapper?.querySelectorAll(
        '[data-promo-frame-selector]'
      );
      this.matchArtworkPromoOptions = this.productCardsWrapper?.querySelectorAll(
        '[data-promo-match-artwork-switch]'
      );

      if (this.framePromoOptions.length > 0)
        this.frameOptions = [...this.frameOptions, ...this.framePromoOptions];
      if (this.matchArtworkPromoOptions.length > 0)
        this.matchArtworkSwitches = [
          ...this.matchArtworkSwitches,
          ...this.matchArtworkPromoOptions,
        ];

      // get the active frame from the sessionStorage or the default frame from the theme settings
      this.currentFrame =
        sessionStorage.currentFrame ||
        this.querySelector('[data-frame-default]')?.dataset?.frameSelector;

      // set the default or pre-selected frame
      this.init();

      // handle click on the frame options
      this.frameOptions.forEach((frameOption) => {
        frameOption.addEventListener('click', () => {
          this.handleFrameSelectorChange(frameOption);
        });
      });

      // handle click on the match artwork switch
      this.matchArtworkSwitches.forEach((matchSwitch) => {
        matchSwitch.addEventListener('click', () => {
          this.handleMatchArtworkClick(matchSwitch);
        });
      });
    }

    /**
     * init
     * @description Set the default or pre-selected frame on the frame options and product cards (if that's the case)
     */
    init() {
      if (!this.currentFrame) return;

      // set the active frame on the frame options
      this.frameOptions.forEach((frameOption) => {
        frameOption.classList.toggle(
          'frame-option--active',
          this.currentFrame == frameOption.dataset.frameSelector
        );
      });

      // update the product cards with the selected frame when it's not the default frame (that one is loaded with liquid)
      if (sessionStorage.currentFrame != undefined) this.updateProductCardsWithSelectedFrame();

      // display the frames on the product cards after the status is updated (to avoid flashing the default)
      this.productCardsWrapper?.classList.remove('product-cards-wrapper--hide-frames');
    }

    /**
     * handleFrameSelectorChange
     * @description Handle the change of the frame options selection
     */
    handleFrameSelectorChange(selectedFrame) {
      // update the variable and the session storage with the selected frame
      this.updateCurrentFrame(selectedFrame.dataset.frameSelector);

      // disable the match artwork switch when a frame is selected
      this.isMatchArtworkEnabled = false;
      this.matchArtworkSwitches.forEach((matchSwitch) => {
        matchSwitch.checked = false;
      });

      // update active class on the frame option buttons
      this.frameOptions.forEach((frame) => {
        frame.classList.toggle(
          'frame-option--active',
          selectedFrame.dataset.frameSelector == frame.dataset.frameSelector
        );
      });

      // update the product cards with the selected frame
      this.updateProductCardsWithSelectedFrame();

      // close drawer on frame click if the user is on a smaller screen
      if (window.innerWidth < 768) this.body.classList.remove('frames-is-open');
    }

    /**
     * updateProductCardsWithSelectedFrame
     * @description Overlay the product cards with the selected frame image
     */
    updateProductCardsWithSelectedFrame() {
      let defaultFrame, square;

      // get the 1 selected Frame when a Frame option has been selected
      if (!this.isMatchArtworkEnabled) {
        ({ defaultFrame, square } = this.getFrameImages(this.currentFrame, true));
      }

      document.querySelectorAll('.product-card__image--frame').forEach((frameImage) => {
        // get the preferred Frame for each product card when the Match artwork option is enabled
        if (this.isMatchArtworkEnabled) {
          const preferredFrameElement = frameImage.closest('[data-preferred-frame]');
          let preferredFrameHandle = preferredFrameElement.dataset.preferredFrame;

          // if there's no preferred frame for the product, show the default one
          if (!preferredFrameHandle) {
            preferredFrameHandle =
              this.querySelector('[data-frame-default]')?.dataset?.frameSelector;
          }

          ({ defaultFrame, square } = this.getFrameImages(preferredFrameHandle));
        }

        // pick the image according to the product card format
        const productCardImage = frameImage.closest('.product-card__image');
        const { productFormat } = productCardImage.dataset;
        const frameImageUrl = productFormat == 'square' ? square : defaultFrame;
        const shadowImage = productCardImage.querySelector('.product-card__image--shadow');

        // if there's no frame image available for the product format, hide the frame image and show the shadow
        frameImage.style.opacity = frameImageUrl == '' ? '0' : '1';
        if (shadowImage) shadowImage.style.opacity = frameImageUrl == '' ? '1' : '0';

        if (!frameImageUrl) return;

        // update the frame image
        frameImage.srcset = frameImageUrl;
      });

      // remove the parent class for no frames when Frames exist
      if (defaultFrame != '' || square != '') {
        this.productCardsWrapper?.classList.remove('product-cards-wrapper--no-frame');
      }
    }

    /**
     * getFrameImages
     * @description Get the selected/preferred frame images for the product cards
     * @param {string} frameHandle
     * @param {boolean} allProductCards
     */
    getFrameImages(frameHandle, allProductCards = false) {
      // get the selected frame from the drawer with all the Frame options
      let selectedFrame = this.querySelector(
        `[data-frame-main-selectors] [data-frame-selector="${frameHandle}"]`
      );

      // set fallback to the default frame, in case the frame is not found in the frames drawer
      if (selectedFrame == null) {
        frameHandle = this.querySelector('[data-frame-default]')?.dataset?.frameSelector;
        selectedFrame = this.querySelector(
          `[data-frame-main-selectors] [data-frame-selector="${frameHandle}"]`
        );
      }

      // set extra guard if there's a pre-selected frame on the user's browser that no longer exists (only for general frame changes, not match artwork)
      if (allProductCards && !selectedFrame?.dataset.frameImageOverlays) {
        // hide frame img when the option 'None' is selected
        this.productCardsWrapper?.classList.add('product-cards-wrapper--no-frame');
        return { defaultFrame: '', square: '' };
      }

      // get the overlay images for the different formats
      const overlayImage = JSON.parse(selectedFrame.dataset.frameImageOverlays);
      const { defaultFrame, square } = overlayImage;

      return { defaultFrame, square };
    }

    /**
     * updateCurrentFrame
     * @description Update the current frame in the session storage and the variable
     * @param {string} newFrame
     */
    updateCurrentFrame(newFrame) {
      this.currentFrame = newFrame;
      sessionStorage.currentFrame = newFrame;
    }

    /**
     * handleMatchArtworkClick
     * @description Update the Match artwork switches state and update the Product cards according to the preferred Frame or previously selected frame
     * @param {object} clickedMatchSwitch
     */
    handleMatchArtworkClick(clickedMatchSwitch) {
      // get the current state of the clicked match artwork switch
      this.isMatchArtworkEnabled = clickedMatchSwitch.checked;

      // update all match artwork switches with the current state
      this.matchArtworkSwitches.forEach((matchSwitch) => {
        matchSwitch.checked = this.isMatchArtworkEnabled;
      });

      if (this.isMatchArtworkEnabled) {
        // switch view to Product when the Match Artwork is enabled
        this.cardOverviewView = document.querySelector('card-overview-view');
        this.cardOverviewView?.switchView(this.cardOverviewView.viewPrimary);

        // deselect the frame options when the match artwork switch is enabled
        this.frameOptions.forEach((frameOption) => {
          frameOption.classList.remove('frame-option--active');
        });

        // update the product cards with the preferred frame for each product
        this.updateProductCardsWithSelectedFrame();
      } else {
        // change the frame selection based on the last selected frame (from session storage or default)
        this.handleFrameSelectorChange(
          this.querySelector(
            `[data-frame-main-selectors] [data-frame-selector="${this.currentFrame}"]`
          )
        );
      }

      // close drawer on match artwork click if the user is on a smaller screen
      if (clickedMatchSwitch.id == 'drawer-match-artwork' && window.innerWidth < 768) {
        const closeDrawer = (e) => {
          if (e.propertyName !== 'transform') return;
          this.body.classList.remove('frames-is-open');
          clickedMatchSwitch.nextElementSibling.removeEventListener('transitionend', closeDrawer);
        };

        clickedMatchSwitch.nextElementSibling.addEventListener('transitionend', closeDrawer);
      }
    }
  }
  customElements.define('product-overview-frame-preview', ProductOverviewFramePreview);
}
